/**
 * Live preview panel — shows the chart payload returned by
 * `useIndicatorPreview` while the user edits the builder.
 *
 * To keep this component simple and avoid coupling to the existing
 * `IndicatorCard` (which fetches its own data via `useIndicatorData`),
 * we render the preview inline using the recharts ResponsiveContainer
 * primitives. A spinner overlay shows while the preview is loading.
 */

import { Loader2 } from 'lucide-react';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent } from '../../../components/ui/card';
import {
  type IndicatorPreviewResponse,
} from '../../../lib/api/reporting';
import { CHART_COLORS } from '../constants';
import { type BuilderChartType } from './ChartTypePicker';

interface PreviewPanelProps {
  preview: IndicatorPreviewResponse | null;
  loading: boolean;
  chartType: BuilderChartType;
}

interface PreviewRow {
  name: string;
  value: number;
  [extra: string]: string | number;
}

function asRows(preview: IndicatorPreviewResponse | null): PreviewRow[] {
  if (!preview || !Array.isArray(preview.data)) return [];
  return preview.data.map((row) => {
    const name = typeof row['name'] === 'string' ? (row['name'] as string) : String(row['name'] ?? '');
    const valueRaw = row['value'];
    const value = typeof valueRaw === 'number' ? valueRaw : Number(valueRaw) || 0;
    return { ...(row as Record<string, string | number>), name, value };
  });
}

function renderGridPreview(preview: IndicatorPreviewResponse | null) {
  const columns = (preview?.['columns'] as Array<{ key?: string; label: string; type?: string }> | undefined) ?? [];
  const gridRows = (preview?.['rows'] as Array<Record<string, unknown>> | undefined) ?? [];
  if (columns.length === 0) {
    return (
      <div className="flex items-center justify-center h-[260px] text-xs text-muted-foreground">
        No preview data yet — build a chart on the left.
      </div>
    );
  }
  const previewRows = gridRows.slice(0, 8);
  return (
    <div className="max-h-[260px] overflow-auto rounded border text-[11px]">
      <table className="w-full">
        <thead className="bg-muted/50 sticky top-0">
          <tr>
            {columns.map((c) => (
              <th key={c.key ?? c.label} className="text-left font-medium px-2 py-1 whitespace-nowrap">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {previewRows.length === 0 ? (
            <tr><td className="px-2 py-3 text-center text-muted-foreground" colSpan={columns.length}>No rows.</td></tr>
          ) : (
            previewRows.map((row, idx) => (
              <tr key={idx} className="border-t">
                {columns.map((c) => {
                  const v = row[c.key ?? c.label];
                  const display = v === null || v === undefined ? '—' : String(v);
                  return (
                    <td key={c.key ?? c.label} className="px-2 py-1 whitespace-nowrap truncate max-w-[120px]" title={display}>
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {gridRows.length > previewRows.length && (
        <div className="px-2 py-1 text-[10px] text-muted-foreground bg-muted/30 border-t">
          Showing {previewRows.length} of {gridRows.length} rows (save to see full grid).
        </div>
      )}
    </div>
  );
}

function getStackKeys(preview: IndicatorPreviewResponse | null): string[] {
  const series = (preview?.['series'] as Record<string, { stack_id?: string }> | undefined) ?? {};
  if (series['value']) return [];
  return Object.entries(series)
    .filter(([, def]) => Boolean(def?.stack_id))
    .map(([key]) => key);
}

function renderChart(rows: PreviewRow[], chartType: BuilderChartType, preview: IndicatorPreviewResponse | null) {
  if (chartType === 'grid') {
    return renderGridPreview(preview);
  }
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-[260px] text-xs text-muted-foreground">
        No preview data yet — build a chart on the left.
      </div>
    );
  }
  // X2 series-split: when the resolver omits `value` and emits stack_id-bearing
  // series, render one bar per key stacked on the same stackId.
  const stackKeys = getStackKeys(preview);
  if ((chartType === 'bar' || chartType === 'waterfall') && stackKeys.length > 0) {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" fontSize={11} interval={0} angle={-25} textAnchor="end" height={60} />
          <YAxis fontSize={11} />
          <Tooltip />
          <Legend />
          {stackKeys.map((key, idx) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="x2"
              fill={CHART_COLORS[idx % CHART_COLORS.length]}
              name={key}
              radius={idx === stackKeys.length - 1 ? [4, 4, 0, 0] : 0}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (chartType === 'pie' || chartType === 'donut') {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={rows}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={90}
            innerRadius={chartType === 'donut' ? 50 : 0}
            label
          >
            {rows.map((_row, i) => (
              <Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" fontSize={11} />
          <YAxis fontSize={11} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  // Default to bar for bar/waterfall/heatmap/grid/kpi/etc.
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={11} interval={0} angle={-25} textAnchor="end" height={60} />
        <YAxis fontSize={11} />
        <Tooltip />
        <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function PreviewPanel({ preview, loading, chartType }: PreviewPanelProps) {
  const rows = asRows(preview);
  return (
    <Card className="relative h-full">
      <CardContent className="pt-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          Preview {preview?.message && <span className="text-[10px] italic">— {preview.message}</span>}
        </div>
        {renderChart(rows, chartType, preview)}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
